import { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    Bell,
    BellOff,
    Layers,
    RefreshCw,
    Send,
    ToggleLeft,
    ToggleRight,
    Zap,
    ZapOff,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { whatsappMerkez } from "../../lib/api";

const KATEGORI_RENK = {
    Kayıt: "border-sky-500/30 bg-sky-500/5",
    Yatar: "border-violet-500/30 bg-violet-500/5",
    "Tahkim zinciri": "border-amber-500/30 bg-amber-500/5",
    Tahkim: "border-orange-500/30 bg-orange-500/5",
    Evrak: "border-emerald-500/30 bg-emerald-500/5",
    Eksper: "border-cyan-500/30 bg-cyan-500/5",
    Süreç: "border-indigo-500/30 bg-indigo-500/5",
    Kapanış: "border-rose-500/30 bg-rose-500/5",
    Ödeme: "border-lime-500/30 bg-lime-500/5",
    Aracı: "border-fuchsia-500/30 bg-fuchsia-500/5",
    Manuel: "border-slate-500/30 bg-slate-500/5",
    Özel: "border-white/10 bg-white/5",
};

function ToggleBtn({ on, onClick, label, iconOn: IconOn, iconOff: IconOff, tone = "emerald" }) {
    const Icon = on ? IconOn : IconOff;
    if (!Icon) return null;
    const cls =
        tone === "emerald"
            ? on
                ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                : "text-slate-400 border-white/10 bg-black/20"
            : on
              ? "text-sky-300 border-sky-500/40 bg-sky-500/10"
              : "text-slate-400 border-white/10 bg-black/20";
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border ${cls}`}
        >
            <Icon className="h-3 w-3" />
            {label}
        </button>
    );
}

export default function WhatsAppBildirimMerkezi({ runBusy, busyKey }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [kategori, setKategori] = useState("");
    const [q, setQ] = useState("");

    const yukle = useCallback(async () => {
        setLoading(true);
        try {
            const res = await whatsappMerkez.bildirimMerkezi();
            setData(res);
        } catch (e) {
            toast.error("Bildirim merkezi yüklenemedi.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        yukle();
    }, [yukle]);

    const sablonlar = useMemo(() => data?.sablonlar || [], [data]);
    const gruplar = useMemo(() => data?.cakisma_gruplari || [], [data]);

    const kategoriler = useMemo(() => {
        const set = new Set(sablonlar.map((s) => s.kategori).filter(Boolean));
        return ["", ...Array.from(set).sort()];
    }, [sablonlar]);

    const filtreli = useMemo(() => {
        let rows = sablonlar;
        if (kategori) rows = rows.filter((s) => s.kategori === kategori);
        if (q.trim()) {
            const t = q.trim().toLowerCase();
            rows = rows.filter(
                (s) =>
                    s.kod?.toLowerCase().includes(t) ||
                    s.ad?.toLowerCase().includes(t) ||
                    s.aciklama?.toLowerCase().includes(t),
            );
        }
        return rows;
    }, [sablonlar, kategori, q]);

    const guncelle = async (kod, patch) => {
        await runBusy(`bildirim-${kod}`, async () => {
            const res = await whatsappMerkez.bildirimToplu({ items: [{ kod, ...patch }] });
            if (res?.ok !== false) {
                setData(res);
            } else {
                toast.error("Güncellenemedi.");
            }
        });
    };

    const topluAktif = async (aktif) => {
        await runBusy("bildirim-toplu", async () => {
            const items = filtreli.map((s) => ({ kod: s.kod, aktif }));
            const res = await whatsappMerkez.bildirimToplu({ items });
            toast.success(aktif ? "Seçili bildirimler açıldı." : "Seçili bildirimler kapatıldı.");
            setData(res);
        });
    };

    const testGonder = async (kod) => {
        await runBusy(`test-${kod}`, async () => {
            const res = await whatsappMerkez.bildirimTest({ sablon_kod: kod });
            if (res?.ok) {
                toast.success(`${kod} test numarasına gönderildi.`);
            } else {
                toast.error(res?.detail || "Test gönderilemedi.");
            }
        });
    };

    return (
        <section className="space-y-4">
            <div className="rounded-xl border border-[#c9a961]/25 bg-gradient-to-br from-[#1a1030]/80 to-[#0b1437]/80 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2">
                            <Bell className="h-4 w-4 text-[#c9a961]" />
                            Bildirim Merkezi — 21 Şablon Kontrolü
                        </h2>
                        <p className="text-xs text-[#8b93ae] mt-1 max-w-3xl">
                            Her WhatsApp bildirimini ayrı ayrı açıp kapatabilirsiniz. Çakışan mesajlar
                            (eksper talebi, evrak uyarısı vb.) otomatik engellenir — aynı dosyaya kısa
                            sürede tekrar gitmez.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={yukle} disabled={loading}>
                            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => topluAktif(true)}>
                            <ToggleRight className="h-3.5 w-3.5 mr-1" />
                            Filtreyi aç
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => topluAktif(false)}>
                            <ToggleLeft className="h-3.5 w-3.5 mr-1" />
                            Filtreyi kapat
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    {[
                        ["Toplam", data?.toplam ?? "—"],
                        ["Aktif", data?.aktif_sayisi ?? "—"],
                        ["Otomatik", data?.otomatik_sayisi ?? "—"],
                        ["Çakışma grubu", gruplar.length || "—"],
                    ].map(([label, val]) => (
                        <div key={label} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                            <div className="text-[10px] text-[#8b93ae] uppercase">{label}</div>
                            <div className="text-xl font-display text-[#F3E5AB]">{val}</div>
                        </div>
                    ))}
                </div>
            </div>

            {gruplar.length > 0 && (
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                    <h3 className="text-xs font-semibold text-amber-200 flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        Çakışma önleme grupları
                    </h3>
                    <div className="grid md:grid-cols-2 gap-2">
                        {gruplar.map((g) => (
                            <div
                                key={g.id}
                                className="rounded-lg border border-amber-500/15 bg-black/20 px-3 py-2 text-[10px]"
                            >
                                <div className="font-semibold text-amber-100">{g.label}</div>
                                <div className="text-[#8b93ae] mt-0.5">{g.not}</div>
                                <div className="text-[#c5cad8] mt-1">
                                    {g.pencere_dk} dk pencere · {g.sablonlar?.join(", ")}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-2 items-center">
                <select
                    value={kategori}
                    onChange={(e) => setKategori(e.target.value)}
                    className="rounded-lg border border-white/10 bg-[#0b1437] px-3 py-1.5 text-xs"
                >
                    {kategoriler.map((k) => (
                        <option key={k || "all"} value={k}>
                            {k || "Tüm kategoriler"}
                        </option>
                    ))}
                </select>
                <input
                    type="search"
                    placeholder="Kod veya ad ara..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="rounded-lg border border-white/10 bg-[#0b1437] px-3 py-1.5 text-xs min-w-[200px]"
                />
            </div>

            <div className="grid gap-3">
                {filtreli.map((s, idx) => (
                    <div
                        key={s.kod}
                        className={`rounded-xl border p-4 ${KATEGORI_RENK[s.kategori] || KATEGORI_RENK.Özel}`}
                    >
                        <div className="flex flex-wrap justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[10px] text-[#8b93ae] font-mono">#{idx + 1}</span>
                                    <h3 className="font-semibold text-[#f5ecd6] text-sm">{s.ad}</h3>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/30 text-[#8b93ae]">
                                        {s.kategori}
                                    </span>
                                </div>
                                <p className="text-[10px] text-violet-300/90 font-mono mt-0.5">{s.kod}</p>
                                <p className="text-xs text-[#c5cad8] mt-1">{s.aciklama}</p>
                                <p className="text-[10px] text-[#8b93ae] mt-1">
                                    Tetik: {s.tetik || "—"}
                                    {s.ayar_anahtari && (
                                        <span className="ml-2 text-sky-300/80">
                                            Ayar: {s.ayar_anahtari}
                                        </span>
                                    )}
                                </p>
                                {s.cakisma_grubu && (
                                    <p className="text-[10px] text-amber-300/90 mt-1 flex items-start gap-1">
                                        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                        Çakışma grubu: {s.cakisma_grubu}
                                        {s.cakisma_notu ? ` — ${s.cakisma_notu}` : ""}
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 items-end shrink-0">
                                <div className="flex flex-wrap gap-1 justify-end">
                                    <ToggleBtn
                                        on={s.aktif}
                                        label={s.aktif ? "Açık" : "Kapalı"}
                                        iconOn={Bell}
                                        iconOff={BellOff}
                                        onClick={() => guncelle(s.kod, { aktif: !s.aktif })}
                                    />
                                    <ToggleBtn
                                        on={s.otomatik_gonder}
                                        label={s.otomatik_gonder ? "Otomatik" : "Manuel"}
                                        iconOn={Zap}
                                        iconOff={ZapOff}
                                        tone="sky"
                                        onClick={() =>
                                            guncelle(s.kod, { otomatik_gonder: !s.otomatik_gonder })
                                        }
                                    />
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[10px]"
                                    disabled={!s.aktif || busyKey === `test-${s.kod}`}
                                    onClick={() => testGonder(s.kod)}
                                >
                                    <Send className="h-3 w-3 mr-1" />
                                    Test gönder
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
